import { reactive, ref, onMounted, onUnmounted } from 'vue';
import { databases, appwriteConfig, query, client } from './appwrite';

const DATABASE_ID = appwriteConfig.database;
const COLLECTION_ID = appwriteConfig.collections.settings;

let instance = null;

export function useSettings() {
    if (instance) return instance;

    const settings = reactive({});
    const isInitialized = ref(false);

    const parseValue = (value, type) => {
        switch (type) {
            case 'number': return Number(value);
            case 'boolean': return value.toLowerCase() === 'true';
            default: return value;
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID);
            response.documents.forEach(doc => {
                settings[doc.key] = parseValue(doc.value, doc.type);
            });
        } catch (err) {
            console.error('Error fetching settings:', err);
        }
    };

    const setSetting = async (key, value, type) => {
        try {
            const stringValue = String(value);
            const documentData = { value: stringValue, type };

            const existingSettings = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
                query.equal('key', key)
            ]);

            if (existingSettings.documents.length > 0) {
                const existingDoc = existingSettings.documents[0];
                await databases.updateDocument(DATABASE_ID, COLLECTION_ID, existingDoc.$id, documentData);
            } else {
                await databases.createDocument(DATABASE_ID, COLLECTION_ID, 'unique()', {
                    key,
                    ...documentData
                });
            }

            settings[key] = parseValue(stringValue, type);
        } catch (err) {
            console.error('Error setting setting:', err);
        }
    };

    const getSetting = (key) => settings[key];

    const initialize = () => {
        if (isInitialized.value) return;

        let unsubscribe = null;
        let reconnectTimeout = null;

        // Create subscription function that can be reused
        const createSubscription = () => {
            // Clean up any existing subscription before creating a new one
            if (unsubscribe) {
                unsubscribe();
            }

            return client.subscribe(
                `databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents`,
                (response) => {
                    if (response.events.some(event => ['documents.*.create', 'documents.*.update'].some(e => event.includes(e)))) {
                        const { key, value, type } = response.payload;
                        settings[key] = parseValue(value, type);
                    }
                },
                // Add error callback
                (error) => {
                    console.error('Settings subscription error:', error);
                    // Clear any existing timeout
                    if (reconnectTimeout) {
                        clearTimeout(reconnectTimeout);
                    }
                    // Attempt to reconnect after 5 seconds
                    reconnectTimeout = setTimeout(() => {
                        console.log('Attempting to reconnect settings...');
                        unsubscribe = createSubscription();
                    }, 5000);
                }
            );
        };

        // Initial subscription
        unsubscribe = createSubscription();

        // Cleanup function that removes the subscription and any pending reconnection
        const cleanup = () => {
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
            }
            if (unsubscribe) {
                unsubscribe();
            }
        };

        onMounted(fetchSettings);
        onUnmounted(cleanup);

        isInitialized.value = true;
    };

    instance = {
        settings,
        setSetting,
        getSetting,
        fetchSettings,
        initialize
    };

    return instance;
}
